import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../api/client';
import { useStores } from '../contexts/StoreContext';
import { 
  ArrowUpTrayIcon, 
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  TrashIcon,
  FunnelIcon,
  DocumentTextIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const CATEGORY_LABELS = {
  comision_pago_movil: { label: 'Comisión Pago Móvil', color: 'bg-orange-100 text-orange-800' },
  comision_bancaria: { label: 'Comisión Bancaria', color: 'bg-orange-100 text-orange-800' },
  comision_transferencia: { label: 'Comisión Transferencia', color: 'bg-orange-100 text-orange-800' },
  comision_pago_proveedores: { label: 'Comisión Pago Proveedores', color: 'bg-orange-100 text-orange-800' },
  comision_mantenimiento: { label: 'Comisión Mantenimiento', color: 'bg-orange-100 text-orange-800' },
  domiciliacion: { label: 'Domiciliación POS', color: 'bg-purple-100 text-purple-800' },
  pago_proveedores: { label: 'Pago Proveedores', color: 'bg-red-100 text-red-800' },
  pago_movil: { label: 'Pago Móvil', color: 'bg-green-100 text-green-800' },
  deposito: { label: 'Depósito', color: 'bg-green-100 text-green-800' },
  transferencia: { label: 'Transferencia', color: 'bg-blue-100 text-blue-800' },
  transferencia_interna: { label: 'Transferencia Interna', color: 'bg-blue-100 text-blue-800' },
  nota_debito: { label: 'Nota de Débito', color: 'bg-gray-100 text-gray-800' },
  prestamo_interno: { label: 'Préstamo Interno', color: 'bg-yellow-100 text-yellow-800' },
  otro: { label: 'Otro', color: 'bg-gray-100 text-gray-800' },
  biopago: { label: 'Biopago', color: 'bg-green-100 text-green-800' },
  comision_biopago: { label: 'Comisión Biopago', color: 'bg-orange-100 text-orange-800' },
  igtf: { label: 'IGTF', color: 'bg-red-100 text-red-800' },
  pos_debito: { label: 'POS Débito', color: 'bg-blue-100 text-blue-800' },
  pos_credito: { label: 'POS Crédito', color: 'bg-indigo-100 text-indigo-800' },
  pago_movil: { label: 'Pago Móvil', color: 'bg-green-100 text-green-800' },
  transferencia: { label: 'Transferencia', color: 'bg-purple-100 text-purple-800' },
  transferencia_interna: { label: 'Transf. entre cuentas', color: 'bg-yellow-100 text-yellow-800' },
  comision_bancaria: { label: 'Comisión bancaria', color: 'bg-red-100 text-red-800' },
  comision_mantenimiento: { label: 'Mant. POS', color: 'bg-red-100 text-red-800' },
  igtf: { label: 'IGTF', color: 'bg-orange-100 text-orange-800' },
  prestamo_interno: { label: 'Préstamo interno', color: 'bg-cyan-100 text-cyan-800' },
  nota_debito: { label: 'Nota de débito', color: 'bg-gray-100 text-gray-800' },
  otro: { label: 'Otro', color: 'bg-gray-100 text-gray-800' },
  sin_clasificar: { label: 'Sin clasificar', color: 'bg-gray-100 text-gray-800' },
};

const STATUS_LABELS = {
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: ExclamationCircleIcon },
  matched: { label: 'Conciliado', color: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
  unmatched: { label: 'Sin cruzar', color: 'bg-red-100 text-red-800', icon: ExclamationCircleIcon },
};

const formatNumber = (num) => {
  if (num === null || num === undefined) return '0,00';
  return new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
};

const BankReconciliation = () => {
  const [activeTab, setActiveTab] = useState('upload'); // upload, movements, comisiones
  const [uploads, setUploads] = useState([]);
  const [movements, setMovements] = useState([]);
  const [summary, setSummary] = useState(null);
  const [comisionesReport, setComisionesReport] = useState(null);
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Stores
  const { stores } = useStores();

  // Upload form
  const [bankName, setBankName] = useState('Banesco');
  const [accountName, setAccountName] = useState('');
  const [storeId, setStoreId] = useState('');
  
  // Reporte
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');

  // Filters
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Conciliacion modal
  const [showConcModal, setShowConcModal] = useState(false);
  const [concMovement, setConcMovement] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [gastos, setGastos] = useState([]);
  const [selectedLotes, setSelectedLotes] = useState([]);
  const [selectedGastos, setSelectedGastos] = useState([]);
  const [concLoading, setConcLoading] = useState(false);
  const [concNotes, setConcNotes] = useState('');
  const [daysRange, setDaysRange] = useState(3);
  const [concTab, setConcTab] = useState('pagos');
  const [incomeSales, setIncomeSales] = useState([]);
  const [incomeTotals, setIncomeTotals] = useState({});
  const [incomeSuggestedFields, setIncomeSuggestedFields] = useState([]);
  const [selectedSaleDates, setSelectedSaleDates] = useState([]);
  const [depositoCandidates, setDepositoCandidates] = useState([]);
  const [selectedDepositos, setSelectedDepositos] = useState([]);


  const loadUploads = useCallback(async () => {
    try {
      const response = await apiClient.get('/bank-reconciliation/uploads');
      setUploads(response.data);
    } catch (err) {
      console.error('Error loading uploads:', err);
    }
  }, []);

  const loadMovements = useCallback(async (uploadId) => {
    setLoading(true);
    try {
      const params = {};
      if (uploadId) params.upload_id = uploadId;
      if (filterType !== 'all') params.movement_type = filterType;
      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterStatus !== 'all') params.matched_status = filterStatus;
      
      const response = await apiClient.get('/bank-reconciliation/movements', { params });
      setMovements(response.data);
      
      if (uploadId) {
        const summaryResponse = await apiClient.get(`/bank-reconciliation/summary/${uploadId}`);
        setSummary(summaryResponse.data);
      }
    } catch (err) {
      console.error('Error loading movements:', err);
    }
    setLoading(false);
  }, [filterType, filterCategory, filterStatus]);

  const handleReclassify = async () => {
    if (!window.confirm('¿Reclasificar todos los movimientos pendientes con el clasificador actualizado?')) return;
    try {
      const response = await apiClient.post('/bank-reconciliation/reclassify');
      setSuccess(`✅ ${response.data.message} — ${response.data.updated} actualizados de ${response.data.total}`);
      if (selectedUpload) loadMovements(selectedUpload);
    } catch (err) {
      setError('Error al reclasificar');
    }
  };

  const loadReport = async () => {
    if (!reportDateFrom || !reportDateTo) return;
    setReportLoading(true);
    try {
      const response = await apiClient.get('/bank-reconciliation/report', {
        params: { date_from: reportDateFrom, date_to: reportDateTo }
      });
      setReportData(response.data);
    } catch (err) {
      setError('Error al generar el reporte');
    }
    setReportLoading(false);
  };

  const exportReportXLSX = () => {
    if (!reportData) return;
    const { resumen, por_banco, comisiones, comision_cat_labels, date_from, date_to } = reportData;

    // Hoja 1: Resumen General
    const resumenRows = [
      ['REPORTE DE CONCILIACIÓN BANCARIA'],
      [`Período: ${date_from} al ${date_to}`],
      [],
      ['RESUMEN GENERAL'],
      ['Concepto', 'Monto (VES)', '%'],
      ['Total Ingresos', resumen.total_ingresos, '100%'],
      ['Total Egresos', resumen.total_egresos, ''],
      ['Total Comisiones', resumen.total_comisiones, resumen.pct_comisiones_sobre_ingresos + '%'],
      [],
      ['CONCILIACIÓN'],
      ['Total Movimientos', resumen.total_movimientos, ''],
      ['Conciliados', resumen.movimientos_conciliados, resumen.pct_conciliados + '%'],
      ['Pendientes', resumen.movimientos_pendientes, (100 - resumen.pct_conciliados).toFixed(1) + '%'],
    ];

    // Hoja 2: Por banco
    const bancoRows = [
      ['DETALLE POR BANCO'],
      ['Banco', 'Ingresos', 'Egresos', 'Comisiones', '% Com/Ing', 'Conciliados', 'Pendientes', '% Conciliado'],
    ];
    Object.entries(por_banco).forEach(([banco, d]) => {
      bancoRows.push([banco, d.total_ingresos, d.total_egresos, d.total_comisiones, d.pct_comisiones + '%', d.movimientos_conciliados, d.movimientos_pendientes, d.pct_conciliados + '%']);
    });

    // Hoja 3: Comisiones detalle
    const comisionRows = [
      ['DETALLE DE COMISIONES POR BANCO'],
      ['Banco', ...Object.values(comision_cat_labels), 'Total'],
    ];
    Object.entries(comisiones).forEach(([banco, d]) => {
      comisionRows.push([banco, ...Object.keys(comision_cat_labels).map(k => d[k] || 0), d.total]);
    });

    // Usar SheetJS via CDN (cargado dinámicamente)
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
    script.onload = () => {
      const wb = window.XLSX.utils.book_new();
      window.XLSX.utils.book_append_sheet(wb, window.XLSX.utils.aoa_to_sheet(resumenRows), 'Resumen');
      window.XLSX.utils.book_append_sheet(wb, window.XLSX.utils.aoa_to_sheet(bancoRows), 'Por Banco');
      window.XLSX.utils.book_append_sheet(wb, window.XLSX.utils.aoa_to_sheet(comisionRows), 'Comisiones');
      window.XLSX.writeFile(wb, `reporte_conciliacion_${date_from}_${date_to}.xlsx`);
    };
    if (!window.XLSX) {
      document.head.appendChild(script);
    } else {
      script.onload();
    }
  };

  const exportReportPDF = () => {
    if (!reportData) return;
    const { resumen, por_banco, comisiones, comision_cat_labels, date_from, date_to } = reportData;

    const loadAndExport = () => {
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF();
      let y = 15;
      const line = (text, x=14, size=10, bold=false) => {
        doc.setFontSize(size);
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        doc.text(text, x, y);
        y += size * 0.5 + 2;
      };
      const fmt = (n) => new Intl.NumberFormat('es-VE', {minimumFractionDigits:2,maximumFractionDigits:2}).format(n);

      line('REPORTE DE CONCILIACIÓN BANCARIA', 14, 14, true);
      line(`Período: ${date_from} al ${date_to}`, 14, 10);
      y += 4;

      line('RESUMEN GENERAL', 14, 12, true);
      y += 2;
      line(`Total Ingresos:      ${fmt(resumen.total_ingresos)} VES`);
      line(`Total Egresos:       ${fmt(resumen.total_egresos)} VES`);
      line(`Total Comisiones:    ${fmt(resumen.total_comisiones)} VES  (${resumen.pct_comisiones_sobre_ingresos}% de los ingresos)`);
      y += 4;

      line('CONCILIACIÓN', 14, 12, true);
      y += 2;
      line(`Total Movimientos:   ${resumen.total_movimientos}`);
      line(`Conciliados:         ${resumen.movimientos_conciliados} (${resumen.pct_conciliados}%)`);
      line(`Pendientes:          ${resumen.movimientos_pendientes} (${(100 - resumen.pct_conciliados).toFixed(1)}%)`);
      y += 6;

      line('DETALLE POR BANCO', 14, 12, true);
      y += 2;
      Object.entries(por_banco).forEach(([banco, d]) => {
        line(`${banco}`, 14, 10, true);
        line(`  Ingresos: ${fmt(d.total_ingresos)}  |  Egresos: ${fmt(d.total_egresos)}  |  Comisiones: ${fmt(d.total_comisiones)} (${d.pct_comisiones}%)`);
        line(`  Conciliados: ${d.movimientos_conciliados}/${d.total_movimientos} (${d.pct_conciliados}%)`);
        y += 2;
        if (y > 260) { doc.addPage(); y = 15; }
      });

      y += 4;
      if (y > 220) { doc.addPage(); y = 15; }
      line('COMISIONES POR BANCO Y TIPO', 14, 12, true);
      y += 2;
      Object.entries(comisiones).forEach(([banco, d]) => {
        line(`${banco}  —  Total: ${fmt(d.total)} VES`, 14, 10, true);
        Object.entries(comision_cat_labels).forEach(([k, label]) => {
          if (d[k] > 0) line(`  ${label}: ${fmt(d[k])} VES`);
        });
        y += 2;
        if (y > 260) { doc.addPage(); y = 15; }
      });

      doc.save(`reporte_conciliacion_${date_from}_${date_to}.pdf`);
    };

    if (!window.jspdf) {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      script.onload = loadAndExport;
      document.head.appendChild(script);
    } else {
      loadAndExport();
    }
  };

  const loadComisiones = useCallback(async () => {
    try {
      const response = await apiClient.get('/bank-reconciliation/comisiones');
      setComisionesReport(response.data);
    } catch (err) {
      console.error('Error loading comisiones:', err);
    }
  }, []);

  useEffect(() => {
    loadUploads();
  }, [loadUploads]);

  useEffect(() => {
    if (selectedUpload) {
      loadMovements(selectedUpload);
    }
  }, [selectedUpload, loadMovements]);

  useEffect(() => {
    if (activeTab === 'comisiones') {
      loadComisiones();
    }
  }, [activeTab, loadComisiones]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    setError('');
    setSuccess('');
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await apiClient.post(
        `/bank-reconciliation/upload?bank_name=${encodeURIComponent(bankName)}&account_name=${encodeURIComponent(accountName)}&store_id=${encodeURIComponent(storeId)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setSuccess(`✅ ${response.data.message}`);
      loadUploads();
      setSelectedUpload(response.data.upload.id);
      setActiveTab('movements');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al subir el archivo');
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDeleteUpload = async (uploadId) => {
    if (!window.confirm('¿Estás seguro? Se eliminarán todos los movimientos de este archivo.')) return;
    
    try {
      await apiClient.delete(`/bank-reconciliation/uploads/${uploadId}`);
      loadUploads();
      if (selectedUpload === uploadId) {
        setSelectedUpload(null);
        setMovements([]);
        setSummary(null);
      }
      setSuccess('Upload eliminado');
    } catch (err) {
      setError('Error al eliminar');
    }
  };

  const handleMatchMovement = async (movementId, status) => {
    try {
      await apiClient.put(`/bank-reconciliation/movements/${movementId}/match`, {
        status: status,
        notes: status === 'matched' ? 'Conciliado manualmente' : ''
      });
      loadMovements(selectedUpload);
    } catch (err) {
      setError('Error al actualizar movimiento');
    }
  };

  const handleUpdateCategory = async (movementId, newCategory) => {
    try {
      await apiClient.put(`/bank-reconciliation/movements/${movementId}/category`, {
        category: newCategory
      });
      loadMovements(selectedUpload);
    } catch (err) {
      setError('Error al actualizar categoría');
    }
  };

  const openConcModal = async (movement) => {
    const isIngreso = movement.amount > 0;
    setConcMovement(movement);
    setShowConcModal(true);
    setSelectedLotes([]);
    setSelectedGastos([]);
    setSelectedSaleDates([]);
    setSelectedDepositos([]);
    setConcNotes('');
    setConcTab(isIngreso ? 'ventas' : 'pagos');
    setConcLoading(true);
    try {
      if (isIngreso) {
        const response = await apiClient.get('/bank-reconciliation/income-candidates', {
          params: { upload_id: movement.upload_id, date: movement.date, days_range: 1 }
        });
        setIncomeSales(response.data.sales || []);
        setIncomeTotals(response.data.totals || {});
        setIncomeSuggestedFields(response.data.suggested_fields || []);
        const depRes = await apiClient.get('/bank-reconciliation/depositos-candidates', {
          params: { date: movement.date, days_range: 3 }
        });
        setDepositoCandidates(depRes.data.depositos || []);
        setCandidates([]);
        setGastos([]);
      } else {
        const response = await apiClient.get('/bank-reconciliation/payment-candidates', {
          params: { date: movement.date, days_range: 3 }
        });
        setCandidates(response.data.lotes || []);
        setGastos(response.data.gastos || []);
        setIncomeSales([]);
        setIncomeTotals({});
      }
    } catch (err) {
      setCandidates([]);
      setGastos([]);
      setIncomeSales([]);
      setIncomeTotals({});
    }
    setConcLoading(false);
  };

  const reloadCandidates = async (d) => {
    if (!concMovement) return;
    setDaysRange(d);
    setConcLoading(true);
    const isIngreso = concMovement.amount > 0;
    try {
      if (isIngreso) {
        const response = await apiClient.get('/bank-reconciliation/income-candidates', {
          params: { upload_id: concMovement.upload_id, date: concMovement.date, days_range: d }
        });
        setIncomeSales(response.data.sales || []);
        setIncomeTotals(response.data.totals || {});
        setIncomeSuggestedFields(response.data.suggested_fields || []);
        const depResponse = await apiClient.get('/bank-reconciliation/depositos-candidates', {
          params: { date: concMovement.date, days_range: d }
        });
        setDepositoCandidates(depResponse.data.depositos || []);
      } else {
        const response = await apiClient.get('/bank-reconciliation/payment-candidates', {
          params: { date: concMovement.date, days_range: d }
        });
        setCandidates(response.data.lotes || []);
        setGastos(response.data.gastos || []);
      }
    } catch(err) { setCandidates([]); setGastos([]); setIncomeSales([]); setIncomeTotals({}); }
    setConcLoading(false);
  };

  const toggleLoteSelection = (ref) => {
    setSelectedLotes(prev => prev.includes(ref) ? prev.filter(r => r !== ref) : [...prev, ref]);
  };

  const toggleGastoSelection = (id) => {
    setSelectedGastos(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
  };

  const handleConciliar = async () => {
    if (!concMovement || (selectedLotes.length === 0 && selectedGastos.length === 0 && selectedSaleDates.length === 0 && selectedDepositos.length === 0)) return;
    try {
      await apiClient.put(
        `/bank-reconciliation/movements/${concMovement.id}/conciliar`,
        { lote_refs: selectedLotes, gasto_ids: selectedGastos, sale_dates: selectedSaleDates, deposito_ids: selectedDepositos, notes: concNotes }
      );
      setShowConcModal(false);
      setSuccess('Movimiento conciliado exitosamente');
      if (selectedUpload) loadMovements(selectedUpload);
    } catch (err) {
      setError('Error al conciliar');
    }
  };

  const getTotalSelected = () => {
    const lotesTotal = candidates.filter(c => selectedLotes.includes(c.reference)).reduce((s,c) => s + c.total_ves, 0);
    const gastosTotal = gastos.filter(g => selectedGastos.includes(g.id)).reduce((s,g) => s + g.amount_ves, 0);
    const salesTotal = incomeSales.filter(s => selectedSaleDates.includes(s.date)).reduce((sum, s) => {
      const fields = incomeSuggestedFields.length > 0 ? incomeSuggestedFields : Object.keys(s.payment_breakdown || {});
      return sum + fields.reduce((a, f) => a + (s.payment_breakdown[f] || 0), 0);
    }, 0);
    return lotesTotal + gastosTotal + salesTotal;
  };

  const toggleSaleDateSelection = (date) => {
    setSelectedSaleDates(prev => prev.includes(date) ? prev.filter(d => d !== date) : [...prev, date]);
  };

  const toggleDepositoSelection = (id) => {
    setSelectedDepositos(prev => prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]);
  };

  const INCOME_FIELD_LABELS = {
    pos_banesco: 'POS Banesco', pos_bdv: 'POS BDV', pos_bnc: 'POS BNC',
    pos_banplus: 'POS Banplus', pos_provincial: 'POS Provincial',
    pago_movil_banesco: 'P.Móvil Banesco', pago_movil_bdv: 'P.Móvil BDV',
    pago_movil_bnc: 'P.Móvil BNC', pago_movil_banplus: 'P.Móvil Banplus',
    biopago: 'Biopago', cashea: 'Cashea', cash_usd: 'Efectivo USD',
    cash_ves: 'Efectivo VES', zelle_transfer: 'Zelle'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Conciliación Bancaria</h1>
          <p className="text-gray-500 text-sm mt-1">Cruza los movimientos bancarios con tu sistema</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {[
            { id: 'upload', label: 'Subir Estado de Cuenta', icon: ArrowUpTrayIcon },
            { id: 'movements', label: 'Movimientos', icon: DocumentTextIcon },
            { id: 'comisiones', label: 'Comisiones Bancarias', icon: BanknotesIcon },
            { id: 'reporte', label: 'Reporte', icon: ChartBarIcon },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
          <button onClick={() => setError('')} className="float-right font-bold">×</button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
          <button onClick={() => setSuccess('')} className="float-right font-bold">×</button>
        </div>
      )}

      {/* TAB: Upload */}
      {activeTab === 'upload' && (
        <div className="space-y-6">
          {/* Upload Form */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Subir Estado de Cuenta</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Banco</label>
                <select
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Banesco">Banesco</option>
                  <option value="BDV">Banco de Venezuela</option>
                  <option value="BNC">Banco Nacional de Crédito</option>
                  <option value="Banplus">Banplus</option>
                  <option value="Provincial">Provincial</option>
                  <option value="Mercantil">Mercantil</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Farmacia</label>
                <select
                  value={storeId}
                  onChange={(e) => setStoreId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- Seleccionar --</option>
                  {stores.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de cuenta (opcional)</label>
                <input
                  type="text"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  placeholder="Ej: Cuenta principal"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Archivo (.xls)</label>
                <label className={`flex items-center justify-center w-full border-2 border-dashed border-gray-300 rounded-lg px-3 py-2 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition ${uploading ? 'opacity-50' : ''}`}>
                  <ArrowUpTrayIcon className="h-5 w-5 mr-2 text-gray-400" />
                  <span className="text-gray-600 text-sm">
                    {uploading ? 'Subiendo...' : 'Seleccionar archivo'}
                  </span>
                  <input
                    type="file"
                    accept=".xls,.xlsx,.csv,.html,.pdf"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Upload History */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Archivos Subidos</h2>
            
            {uploads.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No hay archivos subidos aún</p>
            ) : (
              <div className="space-y-3">
                {uploads.map(upload => (
                  <div 
                    key={upload.id} 
                    className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition ${
                      selectedUpload === upload.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                    onClick={() => { setSelectedUpload(upload.id); setActiveTab('movements'); }}
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <BanknotesIcon className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">
                            {upload.bank_name} {upload.account_name && `- ${upload.account_name}`}
                          </p>
                          <p className="text-sm text-gray-500">
                            {upload.file_name} · {upload.total_movements} movimientos · {upload.period_start} al {upload.period_end}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right text-sm">
                        <p className="text-green-600">+{formatNumber(upload.total_ingresos)}</p>
                        <p className="text-red-600">-{formatNumber(upload.total_egresos)}</p>
                      </div>
                      <div className="text-right text-sm">
                        <p className="text-green-600">{upload.movements_matched} conciliados</p>
                        <p className="text-yellow-600">{upload.movements_unmatched} pendientes</p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteUpload(upload.id); }}
                        className="text-red-400 hover:text-red-600 p-1"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB: Movements */}
      {activeTab === 'movements' && (
        <div className="space-y-4">
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-white rounded-xl shadow-sm border p-4">
                <p className="text-sm text-gray-500">Total Movimientos</p>
                <p className="text-2xl font-bold">{summary.total_movements}</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border p-4">
                <p className="text-sm text-gray-500">Ingresos</p>
                <p className="text-2xl font-bold text-green-600">{formatNumber(summary.total_ingresos)}</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border p-4">
                <p className="text-sm text-gray-500">Egresos</p>
                <p className="text-2xl font-bold text-red-600">{formatNumber(summary.total_egresos)}</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border p-4">
                <p className="text-sm text-gray-500">Comisiones</p>
                <p className="text-2xl font-bold text-orange-600">{formatNumber(summary.comisiones?.total || 0)}</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border p-4">
                <p className="text-sm text-gray-500">Conciliados</p>
                <p className="text-2xl font-bold text-blue-600">{summary.matched} / {summary.total_movements}</p>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center space-x-4 flex-wrap gap-y-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
              
              <select 
                value={selectedUpload || ''} 
                onChange={(e) => setSelectedUpload(e.target.value || null)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
              >
                <option value="">Todos los archivos</option>
                {uploads.map(u => (
                  <option key={u.id} value={u.id}>{u.bank_name} - {u.file_name}</option>
                ))}
              </select>

              <select 
                value={filterType} 
                onChange={(e) => setFilterType(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
              >
                <option value="all">Todos los tipos</option>
                <option value="ingreso">Solo ingresos</option>
                <option value="egreso">Solo egresos</option>
              </select>

              <select 
                value={filterCategory} 
                onChange={(e) => setFilterCategory(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
              >
                <option value="all">Todas las categorías</option>
                {Object.entries(CATEGORY_LABELS).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>

              <select 
                value={filterStatus} 
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
              >
                <option value="all">Todos los estados</option>
                <option value="pending">Pendientes</option>
                <option value="matched">Conciliados</option>
                <option value="unmatched">Sin cruzar</option>
              </select>
              <button
                onClick={handleReclassify}
                className="ml-auto px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                🔄 Reclasificar Pendientes
              </button>
            </div>
          </div>

          {/* Movements Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="text-center py-12 text-gray-500">Cargando movimientos...</div>
            ) : movements.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {selectedUpload ? 'No hay movimientos con estos filtros' : 'Selecciona un archivo subido para ver sus movimientos'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referencia</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoría</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Monto</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {movements.map(movement => {
                      const catInfo = CATEGORY_LABELS[movement.category] || CATEGORY_LABELS.otro;
                      const statusInfo = STATUS_LABELS[movement.matched_status] || STATUS_LABELS.pending;
                      
                      return (
                        <tr key={movement.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{movement.date}</td>
                          <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{movement.reference}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">{movement.description}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <select
                              value={movement.category}
                              onChange={(e) => handleUpdateCategory(movement.id, e.target.value)}
                              className={`text-xs px-2 py-1 rounded-full border-0 ${catInfo.color}`}
                            >
                              {Object.entries(CATEGORY_LABELS).map(([key, val]) => (
                                <option key={key} value={key}>{val.label}</option>
                              ))}
                            </select>
                          </td>
                          <td className={`px-4 py-3 text-sm font-medium text-right whitespace-nowrap ${movement.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {movement.amount >= 0 ? '+' : ''}{formatNumber(movement.amount)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 text-right whitespace-nowrap">
                            {formatNumber(movement.balance)}
                          </td>
                          <td className="px-4 py-3 text-center whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                              {statusInfo.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center whitespace-nowrap">
                            <div className="flex items-center justify-center space-x-1">
                              {movement.matched_status !== 'matched' && (
                                <button
                                  onClick={() => openConcModal(movement)}
                                  className="text-green-500 hover:text-green-700 p-1"
                                  title="Marcar como conciliado"
                                >
                                  <CheckCircleIcon className="h-5 w-5" />
                                </button>
                              )}
                              {movement.matched_status === 'matched' && (
                                <button
                                  onClick={() => handleMatchMovement(movement.id, 'pending')}
                                  className="text-yellow-500 hover:text-yellow-700 p-1"
                                  title="Marcar como pendiente"
                                >
                                  <ExclamationCircleIcon className="h-5 w-5" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB: Comisiones */}
      {activeTab === 'comisiones' && (
        <div className="space-y-6">
          {comisionesReport ? (
            <>
              {/* Total Card */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h2 className="text-lg font-semibold mb-2">Total Comisiones Bancarias</h2>
                <p className="text-4xl font-bold text-red-600">Bs. {formatNumber(comisionesReport.total_comisiones)}</p>
              </div>

              {/* By Bank */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(comisionesReport.by_bank).map(([bank, data]) => (
                  <div key={bank} className="bg-white rounded-xl shadow-sm border p-6">
                    <h3 className="font-semibold text-gray-900 mb-3">{bank}</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Mantenimiento POS</span>
                        <span className="text-red-600">Bs. {formatNumber(data.mantenimiento_pos)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Comisiones transferencia</span>
                        <span className="text-red-600">Bs. {formatNumber(data.comisiones_transferencia)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">IGTF</span>
                        <span className="text-red-600">Bs. {formatNumber(data.igtf)}</span>
                      </div>
                      <hr />
                      <div className="flex justify-between font-semibold">
                        <span>Total</span>
                        <span className="text-red-600">Bs. {formatNumber(data.total)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Movements detail */}
              {comisionesReport.movements?.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                  <div className="p-4 border-b">
                    <h3 className="font-semibold">Detalle de Comisiones</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Banco</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Monto</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {comisionesReport.movements.map(m => (
                          <tr key={m.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">{m.date}</td>
                            <td className="px-4 py-3 text-sm">{m.bank_name}</td>
                            <td className="px-4 py-3 text-sm">{m.description}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${CATEGORY_LABELS[m.category]?.color || ''}`}>
                                {CATEGORY_LABELS[m.category]?.label || m.category}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-red-600 font-medium">
                              Bs. {formatNumber(Math.abs(m.amount))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              Sube un estado de cuenta para ver el reporte de comisiones
            </div>
          )}
        </div>
      )}
      {activeTab === 'reporte' && (
        <div className="space-y-6">
          {/* Filtros de fecha */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Reporte de Conciliación Bancaria</h2>
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
                <input type="date" value={reportDateFrom} onChange={e => setReportDateFrom(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
                <input type="date" value={reportDateTo} onChange={e => setReportDateTo(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500" />
              </div>
              <button onClick={loadReport} disabled={!reportDateFrom || !reportDateTo || reportLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
                {reportLoading ? 'Generando...' : 'Generar Reporte'}
              </button>
              {reportData && (
                <div className="flex gap-2 ml-auto">
                  <button onClick={exportReportXLSX}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 flex items-center gap-2">
                    ⬇ Excel
                  </button>
                  <button onClick={exportReportPDF}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 flex items-center gap-2">
                    ⬇ PDF
                  </button>
                </div>
              )}
            </div>
          </div>

          {reportData && (
            <>
              {/* Resumen General */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-md font-semibold mb-4">Resumen General</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Total Ingresos</p>
                    <p className="text-xl font-bold text-green-600">{formatNumber(reportData.resumen.total_ingresos)}</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Total Egresos</p>
                    <p className="text-xl font-bold text-red-600">{formatNumber(reportData.resumen.total_egresos)}</p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Total Comisiones</p>
                    <p className="text-xl font-bold text-yellow-600">{formatNumber(reportData.resumen.total_comisiones)}</p>
                    <p className="text-xs text-yellow-500 mt-1">{reportData.resumen.pct_comisiones_sobre_ingresos}% de los ingresos</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Conciliación</p>
                    <p className="text-xl font-bold text-blue-600">{reportData.resumen.pct_conciliados}%</p>
                    <p className="text-xs text-blue-500 mt-1">{reportData.resumen.movimientos_conciliados}/{reportData.resumen.total_movimientos} movimientos</p>
                  </div>
                </div>
              </div>

              {/* Por banco */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-md font-semibold mb-4">Detalle por Banco</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-gray-50">
                      <th className="px-4 py-2 text-left">Banco</th>
                      <th className="px-4 py-2 text-right">Ingresos</th>
                      <th className="px-4 py-2 text-right">Egresos</th>
                      <th className="px-4 py-2 text-right">Comisiones</th>
                      <th className="px-4 py-2 text-right">% Com/Ing</th>
                      <th className="px-4 py-2 text-center">Conciliados</th>
                      <th className="px-4 py-2 text-center">Pendientes</th>
                      <th className="px-4 py-2 text-center">% Conc.</th>
                    </tr></thead>
                    <tbody>
                      {Object.entries(reportData.por_banco || {}).map(([banco, d], idx) => (
                        <tr key={banco} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-2 font-medium">{banco}</td>
                          <td className="px-4 py-2 text-right text-green-600">{formatNumber(d.total_ingresos)}</td>
                          <td className="px-4 py-2 text-right text-red-600">{formatNumber(d.total_egresos)}</td>
                          <td className="px-4 py-2 text-right text-yellow-600">{formatNumber(d.total_comisiones)}</td>
                          <td className="px-4 py-2 text-right">
                            <span className={`font-bold ${d.pct_comisiones > 2 ? 'text-red-500' : 'text-green-600'}`}>{d.pct_comisiones}%</span>
                          </td>
                          <td className="px-4 py-2 text-center text-green-600">{d.movimientos_conciliados}</td>
                          <td className="px-4 py-2 text-center text-red-500">{d.movimientos_pendientes}</td>
                          <td className="px-4 py-2 text-center">
                            <div className="flex items-center gap-1 justify-center">
                              <div className="w-16 h-2 bg-gray-200 rounded-full">
                                <div style={{width: `${d.pct_conciliados}%`}} className={`h-full rounded-full ${d.pct_conciliados >= 80 ? 'bg-green-500' : d.pct_conciliados >= 50 ? 'bg-yellow-400' : 'bg-red-400'}`}></div>
                              </div>
                              <span className="text-xs font-bold">{d.pct_conciliados}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Comisiones detalle */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-md font-semibold mb-4">Detalle de Comisiones por Banco y Tipo</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-gray-50">
                      <th className="px-4 py-2 text-left">Banco</th>
                      {Object.values(reportData.comision_cat_labels || {}).map(label => (
                        <th key={label} className="px-4 py-2 text-right">{label}</th>
                      ))}
                      <th className="px-4 py-2 text-right font-bold">Total</th>
                      <th className="px-4 py-2 text-right">% /Ingresos</th>
                    </tr></thead>
                    <tbody>
                      {Object.entries(reportData.comisiones || {}).map(([banco, d], idx) => {
                        const ingBanco = reportData.por_banco[banco]?.total_ingresos || 0;
                        const pct = ingBanco > 0 ? ((d.total / ingBanco) * 100).toFixed(2) : '0.00';
                        return (
                          <tr key={banco} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-4 py-2 font-medium">{banco}</td>
                            {Object.keys(reportData.comision_cat_labels || {}).map(k => (
                              <td key={k} className="px-4 py-2 text-right">{d[k] > 0 ? formatNumber(d[k]) : <span className="text-gray-300">—</span>}</td>
                            ))}
                            <td className="px-4 py-2 text-right font-bold text-yellow-600">{formatNumber(d.total)}</td>
                            <td className="px-4 py-2 text-right">
                              <span className={`font-bold ${parseFloat(pct) > 2 ? 'text-red-500' : 'text-green-600'}`}>{pct}%</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {!reportData && !reportLoading && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center text-gray-400">
              <ChartBarIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Selecciona un rango de fechas y genera el reporte</p>
            </div>
          )}
        </div>
      )}

      {showConcModal && concMovement && (
  <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,backgroundColor:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
    <div style={{backgroundColor:'white',borderRadius:'12px',padding:'24px',width:'90%',maxWidth:'800px',maxHeight:'80vh',overflow:'auto'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px'}}>
        <h3 style={{margin:0}}>Conciliar Movimiento Bancario</h3>
        <button onClick={() => setShowConcModal(false)} style={{background:'none',border:'none',fontSize:'20px',cursor:'pointer'}}>✕</button>
      </div>
      <div style={{backgroundColor:'#f8f9fa',borderRadius:'8px',padding:'12px',marginBottom:'16px'}}>
        <p style={{margin:'4px 0'}}><strong>Fecha:</strong> {concMovement.date}</p>
        <p style={{margin:'4px 0'}}><strong>Descripción:</strong> {concMovement.description}</p>
        <p style={{margin:'4px 0'}}><strong>Monto:</strong> <span style={{color: concMovement.amount < 0 ? '#dc3545' : '#28a745',fontWeight:'bold'}}>{formatNumber(concMovement.amount)}</span></p>
        <p style={{margin:'4px 0'}}><strong>Referencia:</strong> {concMovement.reference}</p>
      </div>
      <div style={{marginBottom:'12px',display:'flex',gap:'12px',alignItems:'center',flexWrap:'wrap'}}>
        <label>Rango: </label>
        <select value={daysRange} onChange={e => reloadCandidates(parseInt(e.target.value))} style={{padding:'4px 8px',borderRadius:'4px',border:'1px solid #ddd'}}>
          <option value={1}>±1 día</option>
          <option value={3}>±3 días</option>
          <option value={7}>±7 días</option>
          <option value={15}>±15 días</option>
        </select>
        {(selectedLotes.length > 0 || selectedGastos.length > 0) && (
          <span style={{color:'#28a745',fontWeight:'bold'}}>{selectedLotes.length + selectedGastos.length} seleccionado(s) | Total: {formatNumber(getTotalSelected())}</span>
        )}
      </div>
      <div style={{display:'flex',gap:'0',marginBottom:'12px',borderBottom:'2px solid #eee'}}>
        {concMovement && concMovement.amount > 0 ? (
          <><button onClick={() => setConcTab('ventas')} style={{padding:'8px 16px',border:'none',cursor:'pointer',fontWeight:'bold',borderBottom:concTab==='ventas'?'2px solid #28a745':'none',color:concTab==='ventas'?'#28a745':'#666',background:'none'}}>Ventas ({incomeSales.length})</button>
          <button onClick={() => setConcTab('depositos')} style={{padding:'8px 16px',border:'none',cursor:'pointer',fontWeight:'bold',borderBottom:concTab==='depositos'?'2px solid #0d6efd':'none',color:concTab==='depositos'?'#0d6efd':'#666',background:'none'}}>Depósitos ({depositoCandidates.length})</button></>
        ) : (
          <>
            <button onClick={() => setConcTab('pagos')} style={{padding:'8px 16px',border:'none',cursor:'pointer',fontWeight:'bold',borderBottom:concTab==='pagos'?'2px solid #007bff':'none',color:concTab==='pagos'?'#007bff':'#666',background:'none'}}>Pagos ({candidates.length})</button>
            <button onClick={() => setConcTab('gastos')} style={{padding:'8px 16px',border:'none',cursor:'pointer',fontWeight:'bold',borderBottom:concTab==='gastos'?'2px solid #007bff':'none',color:concTab==='gastos'?'#007bff':'#666',background:'none'}}>Gastos ({gastos.length})</button>
          </>
        )}
      </div>
      {concLoading ? <p>Buscando...</p> : concTab === 'depositos' ? (
        depositoCandidates.length === 0 ? (
          <div style={{color:'#999',padding:'16px',textAlign:'center'}}>
            <p>No se encontraron depósitos de efectivo en este rango.</p>
            <p style={{fontSize:'12px',marginTop:'4px'}}>Registra un depósito desde Control de Efectivo.</p>
          </div>
        ) : (
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'14px'}}>
            <thead><tr style={{backgroundColor:'#f1f3f5'}}>
              <th style={{padding:'8px',textAlign:'left'}}>✓</th>
              <th style={{padding:'8px',textAlign:'left'}}>Fecha</th>
              <th style={{padding:'8px',textAlign:'right'}}>Monto (Bs.)</th>
              <th style={{padding:'8px',textAlign:'right'}}>Monto Banco</th>
              <th style={{padding:'8px',textAlign:'center'}}>Diferencia</th>
              <th style={{padding:'8px',textAlign:'left'}}>Referencia</th>
              <th style={{padding:'8px',textAlign:'left'}}>Notas</th>
            </tr></thead>
            <tbody>{depositoCandidates.map((dep, idx) => {
              const isSelected = selectedDepositos.includes(dep.id);
              const diff = concMovement ? dep.amount_ves - Math.abs(concMovement.amount) : 0;
              return (
                <tr key={idx} onClick={() => toggleDepositoSelection(dep.id)} style={{cursor:'pointer',backgroundColor:isSelected?'#d4edda':idx%2===0?'white':'#f8f9fa',borderBottom:'1px solid #eee'}}>
                  <td style={{padding:'8px'}}><input type="checkbox" checked={isSelected} onChange={() => toggleDepositoSelection(dep.id)} /></td>
                  <td style={{padding:'8px'}}>{dep.movement_date}</td>
                  <td style={{padding:'8px',textAlign:'right',fontWeight:'bold'}}>{formatNumber(dep.amount_ves)}</td>
                  <td style={{padding:'8px',textAlign:'right',color:'#666'}}>{concMovement ? formatNumber(Math.abs(concMovement.amount)) : '-'}</td>
                  <td style={{padding:'8px',textAlign:'center'}}>
                    <span style={{fontSize:'12px',fontWeight:'bold',color:Math.abs(diff)<1?'#28a745':Math.abs(diff)<dep.amount_ves*0.05?'#ffc107':'#dc3545'}}>
                      {Math.abs(diff)<1?'✓ Exacto':diff>0?`+${formatNumber(diff)}`:formatNumber(diff)}
                    </span>
                  </td>
                  <td style={{padding:'8px',fontSize:'12px'}}>{dep.reference || '-'}</td>
                  <td style={{padding:'8px',fontSize:'12px'}}>{dep.notes || '-'}</td>
                </tr>
              );
            })}</tbody>
          </table>
        )
      ) : concTab === 'ventas' ? (
        incomeSales.length === 0 ? (
          <div style={{color:'#999',padding:'16px',textAlign:'center'}}>
            <p>No se encontraron ventas en este rango.</p>
            <p style={{fontSize:'12px',marginTop:'4px'}}>Verifica que el upload tenga una farmacia asociada.</p>
          </div>
        ) : (
          <div>
            <p style={{fontSize:'13px',color:'#666',marginBottom:'12px'}}>
              Farmacia: <strong>{incomeSales[0]?.store_name}</strong> · 
              Campos sugeridos: <strong>{incomeSuggestedFields.map(f => INCOME_FIELD_LABELS[f] || f).join(', ') || 'Todos'}</strong>
            </p>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'14px'}}>
              <thead><tr style={{backgroundColor:'#f1f3f5'}}>
                <th style={{padding:'8px',textAlign:'left'}}>✓</th>
                <th style={{padding:'8px',textAlign:'left'}}>Fecha</th>
                {incomeSuggestedFields.map(f => (
                  <th key={f} style={{padding:'8px',textAlign:'right'}}>{INCOME_FIELD_LABELS[f] || f}</th>
                ))}
                <th style={{padding:'8px',textAlign:'right',color:'#28a745'}}>Total sugerido</th>
                <th style={{padding:'8px',textAlign:'right',color:'#666',fontSize:'12px'}}>Monto banco</th>
                <th style={{padding:'8px',textAlign:'center'}}>Diferencia</th>
              </tr></thead>
              <tbody>{incomeSales.map((sale, idx) => {
                const suggestedTotal = incomeSuggestedFields.reduce((a, f) => a + (sale.payment_breakdown[f] || 0), 0);
                const diff = concMovement ? suggestedTotal - Math.abs(concMovement.amount) : 0;
                const isSelected = selectedSaleDates.includes(sale.date);
                return (
                  <tr key={idx} onClick={() => toggleSaleDateSelection(sale.date)} style={{cursor:'pointer',backgroundColor:isSelected?'#d4edda':idx%2===0?'white':'#f8f9fa',borderBottom:'1px solid #eee'}}>
                    <td style={{padding:'8px'}}><input type="checkbox" checked={isSelected} onChange={() => toggleSaleDateSelection(sale.date)} /></td>
                    <td style={{padding:'8px',fontWeight:'bold'}}>{sale.date}</td>
                    {incomeSuggestedFields.map(f => (
                      <td key={f} style={{padding:'8px',textAlign:'right'}}>{formatNumber(sale.payment_breakdown[f] || 0)}</td>
                    ))}
                    <td style={{padding:'8px',textAlign:'right',fontWeight:'bold',color:'#28a745'}}>{formatNumber(suggestedTotal)}</td>
                    <td style={{padding:'8px',textAlign:'right',fontSize:'12px',color:'#666'}}>{concMovement ? formatNumber(Math.abs(concMovement.amount)) : '-'}</td>
                    <td style={{padding:'8px',textAlign:'center'}}>
                      <span style={{fontSize:'12px',fontWeight:'bold',color:Math.abs(diff) < 1 ? '#28a745' : Math.abs(diff) < suggestedTotal * 0.05 ? '#ffc107' : '#dc3545'}}>
                        {Math.abs(diff) < 1 ? '✓ Cuadra' : diff > 0 ? `+${formatNumber(diff)}` : formatNumber(diff)}
                      </span>
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        )
      ) : concTab === 'pagos' ? (
        candidates.length === 0 ? <p style={{color:'#999'}}>No se encontraron pagos</p> : (
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'14px'}}>
            <thead><tr style={{backgroundColor:'#f1f3f5'}}>
              <th style={{padding:'8px',textAlign:'left'}}>✓</th>
              <th style={{padding:'8px',textAlign:'left'}}>Fecha</th>
              <th style={{padding:'8px',textAlign:'left'}}>Referencia</th>
              <th style={{padding:'8px',textAlign:'left'}}>Proveedores</th>
              <th style={{padding:'8px',textAlign:'right'}}>Monto VES</th>
              <th style={{padding:'8px',textAlign:'center'}}>Fact.</th>
              <th style={{padding:'8px',textAlign:'right'}}>Pagado</th>
              <th style={{padding:'8px',textAlign:'center'}}>Progreso</th>
            </tr></thead>
            <tbody>{candidates.map((lote, idx) => (
              <tr key={idx} onClick={() => toggleLoteSelection(lote.reference)} style={{cursor:'pointer',backgroundColor:selectedLotes.includes(lote.reference)?'#d4edda':idx%2===0?'white':'#f8f9fa',borderBottom:'1px solid #eee'}}>
                <td style={{padding:'8px'}}><input type="checkbox" checked={selectedLotes.includes(lote.reference)} onChange={() => toggleLoteSelection(lote.reference)} /></td>
                <td style={{padding:'8px'}}>{lote.date}</td>
                <td style={{padding:'8px',fontFamily:'monospace',fontSize:'12px'}}>{lote.reference}</td>
                <td style={{padding:'8px',fontSize:'12px'}}>{lote.suppliers.slice(0,2).join(', ')}{lote.suppliers.length > 2 && ` +${lote.suppliers.length-2}`}</td>
                <td style={{padding:'8px',textAlign:'right',fontWeight:'bold'}}>{formatNumber(lote.total_ves)}</td>
                <td style={{padding:'8px',textAlign:'center'}}>{lote.invoice_count}</td>
                <td style={{padding:'8px',textAlign:'right',fontSize:'12px'}}>{lote.paid_banco > 0 ? formatNumber(lote.paid_banco) : '-'}</td>
                <td style={{padding:'8px',textAlign:'center'}}>
                  {lote.porcentaje > 0 ? (
                    <div style={{display:'flex',alignItems:'center',gap:'4px'}}>
                      <div style={{flex:1,height:'8px',backgroundColor:'#e9ecef',borderRadius:'4px',minWidth:'60px'}}>
                        <div style={{width:`${Math.min(lote.porcentaje,100)}%`,height:'100%',backgroundColor:lote.porcentaje>=99.5?'#28a745':lote.porcentaje>50?'#ffc107':'#dc3545',borderRadius:'4px'}}></div>
                      </div>
                      <span style={{fontSize:'11px',fontWeight:'bold',color:lote.porcentaje>=99.5?'#28a745':'#666'}}>{lote.porcentaje}%</span>
                    </div>
                  ) : <span style={{color:'#999',fontSize:'12px'}}>Sin pago</span>}
                </td>
              </tr>
            ))}</tbody>
          </table>
        )
      ) : (
        gastos.length === 0 ? <p style={{color:'#999'}}>No se encontraron gastos</p> : (
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'14px'}}>
            <thead><tr style={{backgroundColor:'#f1f3f5'}}>
              <th style={{padding:'8px',textAlign:'left'}}>✓</th>
              <th style={{padding:'8px',textAlign:'left'}}>Fecha</th>
              <th style={{padding:'8px',textAlign:'left'}}>Concepto</th>
              <th style={{padding:'8px',textAlign:'right'}}>Monto VES</th>
              <th style={{padding:'8px',textAlign:'right'}}>Monto USD</th>
              <th style={{padding:'8px',textAlign:'left'}}>Categoría</th>
            </tr></thead>
            <tbody>{gastos.map((gasto, idx) => (
              <tr key={idx} onClick={() => toggleGastoSelection(gasto.id)} style={{cursor:'pointer',backgroundColor:selectedGastos.includes(gasto.id)?'#d4edda':idx%2===0?'white':'#f8f9fa',borderBottom:'1px solid #eee'}}>
                <td style={{padding:'8px'}}><input type="checkbox" checked={selectedGastos.includes(gasto.id)} onChange={() => toggleGastoSelection(gasto.id)} /></td>
                <td style={{padding:'8px'}}>{gasto.date}</td>
                <td style={{padding:'8px'}}>{gasto.concept}</td>
                <td style={{padding:'8px',textAlign:'right',fontWeight:'bold'}}>{formatNumber(gasto.amount_ves)}</td>
                <td style={{padding:'8px',textAlign:'right'}}>{formatNumber(gasto.amount_usd)}</td>
                <td style={{padding:'8px',fontSize:'12px'}}>{gasto.category}</td>
              </tr>
            ))}</tbody>
          </table>
        )
      )}
      <div style={{marginTop:'16px'}}>
        <label>Notas:</label>
        <input type="text" value={concNotes} onChange={e => setConcNotes(e.target.value)} placeholder="Ej: Pago lote semanal" style={{width:'100%',padding:'8px',borderRadius:'4px',border:'1px solid #ddd',marginTop:'4px'}} />
      </div>
      <div style={{marginTop:'16px',display:'flex',gap:'12px',justifyContent:'flex-end'}}>
        <button onClick={() => setShowConcModal(false)} style={{padding:'8px 16px',borderRadius:'6px',border:'1px solid #ddd',cursor:'pointer'}}>Cancelar</button>
        <button onClick={handleConciliar} disabled={selectedLotes.length===0 && selectedGastos.length===0 && selectedSaleDates.length===0 && selectedDepositos.length===0} style={{padding:'8px 16px',borderRadius:'6px',border:'none',backgroundColor:(selectedLotes.length>0||selectedGastos.length>0||selectedSaleDates.length>0||selectedDepositos.length>0)?'#28a745':'#ccc',color:'white',cursor:(selectedLotes.length>0||selectedGastos.length>0||selectedSaleDates.length>0||selectedDepositos.length>0)?'pointer':'not-allowed',fontWeight:'bold'}}>Conciliar ({selectedLotes.length+selectedGastos.length+selectedSaleDates.length+selectedDepositos.length})</button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default BankReconciliation;
